import React, { createContext, useContext, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import BackButton from './BackButton';
import PageHeader from './PageHeader';
import { cn } from './cn';

const LayoutContext = createContext(null);

/** Layout-owned destinations keep page controls in place without lifting feature state. */
function PageLayout({ children }) {
  const [actions, setActions] = useState(null);
  const [heading, setHeading] = useState(null);
  const value = useMemo(() => ({ actions, heading, setActions, setHeading }), [actions, heading]);
  return <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>;
}

/** Shared client-page measure; the surrounding frame supplies responsive gutters. */
function Container({ as: Component = 'div', children, className, ...props }) {
  return <Component className={cn('mx-auto min-w-0 w-full max-w-7xl', className)} {...props}>{children}</Component>;
}

/** Client pages share one inset below navigation, including loading and detail views. */
function Frame({ as: Component = 'main', children, className, ...props }) {
  return <Component className={cn('box-border w-full px-4 pt-6 pb-[72px] sm:px-8 xl:px-11', className)} {...props}>
    <Container>{children}</Container>
  </Component>;
}

function Actions({ children, className }) {
  const layout = useContext(LayoutContext);
  return <div ref={layout.setActions} role="group" aria-label="Page actions"
    className={cn('flex min-w-0 flex-wrap items-center justify-end gap-2 empty:hidden', className)}>{children}</div>;
}

function Heading({ className }) {
  const layout = useContext(LayoutContext);
  return <div ref={layout.setHeading} className={cn('empty:hidden', className)} />;
}

/** Client headers and mobile advisor detail headers keep titles and actions together. */
function HeaderRow({ children, className, variant }) {
  if (variant === 'client') return <div role="group" aria-label="Page header"
    className={cn('flex flex-wrap items-start gap-x-4 gap-y-3 pb-6 [&:not(:has(>div:not(:empty)))]:hidden', className)}>
    <Heading className="peer min-w-0 flex-1" />
    <Actions className="ml-auto max-w-[50%] peer-empty:max-w-full">{children}</Actions>
  </div>;
  return <div role="group" aria-label="Page header" className={cn('flex items-start gap-2 pt-5', className)}>
    <Heading className="peer min-w-0 flex-1 pb-3" />
    <Actions className="ml-auto max-w-[50%] pb-4 peer-empty:max-w-full">{children}</Actions>
  </div>;
}

/** Client pages supply a title; detail pages also supply an explicit back destination. */
function Header({ title, subtitle, onBack, backLabel = 'Back', actions, size, truncateTitle }) {
  const layout = useContext(LayoutContext);
  const heading = title ? <PageHeader title={title} subtitle={subtitle} size={size} truncateTitle={truncateTitle}
    back={onBack ? <BackButton labelHidden onClick={onBack}>{backLabel}</BackButton> : undefined} /> : null;
  if (!layout) {
    return <>{heading}{actions && <div className="flex flex-wrap items-center justify-end gap-2">{actions}</div>}</>;
  }
  return <>
    {layout.heading && heading && createPortal(heading, layout.heading)}
    {layout.actions && actions && createPortal(actions, layout.actions)}
  </>;
}

PageLayout.Container = Container;
PageLayout.Frame = Frame;
PageLayout.Actions = Actions;
PageLayout.Heading = Heading;
PageLayout.HeaderRow = HeaderRow;
PageLayout.Header = Header;
export default PageLayout;
